/**
 * Canonical Coverage route: /cycles/:cycleId/coverage
 *   ?entity=&boundary=&framework=&requirement=&metric=&lens=
 *
 * The ONE data-bearing coverage surface (docs/COVERAGE_ROUTE_CONTRACT.md).
 * Validates cycle, entity, and boundary against the tenant-scoped selection
 * universe BEFORE any coverage fetch; rejects invalid parameters with typed
 * states — never a fallback to the first option. Direct loads and browser
 * back/forward work because all state lives in the URL (user changes push,
 * so history restores prior context).
 */
import { useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, Lock, CircleDashed } from "lucide-react";
import {
  C, MONO, SANS, HEAD, Data, Card, Skeleton, ErrorBanner, Btn,
} from "../../components/esg/EsgKit";
import useCoverageContext from "./useCoverageContext";
import CoverageView from "./CoverageView";

function StateCard({ Icon, color, title, body, testid, action }) {
  return (
    <Card style={{ maxWidth: 560, margin: "40px auto", textAlign: "center", padding: 28 }}
          data-testid={testid}>
      <Icon size={22} strokeWidth={1.5} color={color} style={{ marginBottom: 10 }} />
      <h2 style={{ fontFamily: HEAD, fontSize: 17, fontWeight: 700, color: C.ink, margin: "0 0 6px" }}>
        {title}
      </h2>
      <p style={{ fontFamily: SANS, fontSize: 13, color: C.body, margin: 0 }}>{body}</p>
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </Card>
  );
}

function Chip({ label, value }) {
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "baseline",
                   border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px" }}>
      <span style={{ fontFamily: SANS, fontSize: 10, color: C.muted, textTransform: "uppercase",
                     letterSpacing: "0.5px" }}>{label}</span>
      <span style={{ fontFamily: MONO, fontSize: 11, color: C.ink }}>{value}</span>
    </span>
  );
}

export default function CycleCoveragePage() {
  const { cycleId } = useParams();
  const [params, setParams] = useSearchParams();
  const entityId = params.get("entity") || "";
  const boundaryId = params.get("boundary") || "";
  const highlight = params.get("metric") || "";

  const ctx = useCoverageContext();

  // User-driven state changes PUSH (not replace) so back/forward restores
  // the previous context — a contract requirement.
  const setParam = useCallback((k, v) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v) next.set(k, v); else next.delete(k);
      return next;
    });
  }, [setParams]);

  const resolverHref = `/coverage?${new URLSearchParams({
    ...(cycleId ? { cycle: cycleId } : {}),
    ...(entityId ? { entity: entityId } : {}),
    ...(boundaryId ? { boundary: boundaryId } : {}),
  }).toString()}`;

  let body;
  if (ctx.error?.status === 403) {
    body = <StateCard Icon={Lock} color={C.destructive} testid="coverage-permission-denied"
                      title="Permission denied"
                      body="Your role does not grant access to the coverage context for this tenant. This is an access decision, not an empty result." />;
  } else if (ctx.error) {
    body = (
      <div style={{ maxWidth: 560, margin: "40px auto" }}>
        <ErrorBanner message={`Could not load coverage context: ${ctx.error.message}`} />
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <Btn variant="secondary" onClick={() => window.location.reload()}>Retry</Btn>
        </div>
      </div>
    );
  } else if (ctx.loading) {
    body = <div style={{ padding: 20 }} data-testid="context-loading"><Skeleton h={220} /></div>;
  } else {
    const v = ctx.validate(cycleId, entityId, boundaryId);
    if (!v.ok && v.type === "incomplete_context") {
      body = <StateCard Icon={CircleDashed} color={C.muted} testid="coverage-context-incomplete"
                        title="Coverage context incomplete"
                        body={`Coverage needs an explicit reporting cycle, entity, and boundary — missing: ${v.missing.join(", ")}. Nothing is assumed on your behalf.`}
                        action={<Link to={resolverHref} style={{ fontFamily: SANS, fontSize: 13, color: C.locked }}>
                          Select coverage context →</Link>} />;
    } else if (!v.ok) {
      const label = { invalid_cycle: "Unknown reporting cycle",
                      invalid_entity: "Unknown or unauthorized entity",
                      invalid_boundary: "Unknown reporting boundary" }[v.type];
      body = <StateCard Icon={AlertTriangle} color={C.destructive} testid={`coverage-${v.type}`}
                        title={label}
                        body={`"${v.id}" is not in your tenant's ${v.type.replace("invalid_", "")} registry. No fallback is applied — verify the link or choose a valid context.`}
                        action={<Link to={resolverHref} style={{ fontFamily: SANS, fontSize: 13, color: C.locked }}>
                          Select coverage context →</Link>} />;
    } else {
      const cycle = ctx.findCycle(cycleId);
      const entity = ctx.findEntity(entityId);
      const boundary = ctx.resolveBoundary(boundaryId);
      body = (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}
               data-testid="context-chips">
            <Chip label="Cycle" value={cycle.label || cycle.cycle_id} />
            <Chip label="Entity" value={entity.name || entity.entity_id} />
            <Chip label="Boundary"
                  value={`${boundary.label || boundary.boundary_type} · ${boundary.boundary_id} v${boundary.version}`} />
          </div>
          <CoverageView entityId={entityId} cycleId={cycleId} boundary={boundary}
                        highlight={highlight} onHighlight={(m) => setParam("metric", m)} />
        </>
      );
    }
  }

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "28px 32px",
                  fontFamily: SANS, maxWidth: 1240, margin: "0 auto" }}>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: HEAD, fontSize: 26, fontWeight: 700, color: C.ink,
                     letterSpacing: "-0.02em", margin: 0, textWrap: "balance" }}>
          Evidence Coverage
        </h1>
        <p style={{ fontFamily: SANS, fontSize: 13, color: C.body, margin: "6px 0 0",
                    maxWidth: "68ch" }}>
          See what your uploaded evidence supports — and what to provide next.
          Confirmed means a reviewer accepted the mapping; Defended appears only
          when every applicable element is complete, current and reviewed.
        </p>
      </header>
      {body}
    </div>
  );
}
